import { useState } from 'react';
import { Header } from '../../components/feature/Header';
import { BottomNav } from '../../components/feature/BottomNav';
import { WhatsAppFloat } from '../../components/feature/WhatsAppFloat';
import { Button } from '../../components/base/Button';
import { Card } from '../../components/base/Card';
import { SEOHead } from '../../components/seo/SEOHead';

export default function ContactoPage() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    service: '',
    message: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';

  const schema = {
    "@context": "https://schema.org",
    "@type": "ContactPage",
    "name": "Contact CleanPro Services",
    "description": "Get in touch with CleanPro Services for professional cleaning services",
    "url": `${siteUrl}/contacto`,
    "mainEntity": {
      "@type": "LocalBusiness",
      "name": "CleanPro Services",
      "telephone": "9184078932",
      "email": "accesorios7g@gmail.com",
      "address": {
        "@type": "PostalAddress",
        "addressCountry": "US"
      }
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formDataToSend = new FormData();
      formDataToSend.append('name', formData.name);
      formDataToSend.append('email', formData.email);
      formDataToSend.append('phone', formData.phone);
      formDataToSend.append('service', formData.service);
      formDataToSend.append('message', formData.message);
      formDataToSend.append('recipient', 'accesorios7g@gmail.com');

      const response = await fetch('https://readdy.ai/api/form/d3vdndab8acecn2ovdrg', {
        method: 'POST',
        body: formDataToSend
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          name: '',
          email: '',
          phone: '',
          service: '',
          message: ''
        });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-green-50 pt-20 pb-24">
      <SEOHead
        title="Contact Us - CleanPro Services | Get Your Free Quote"
        description="Contact CleanPro Services for professional cleaning services. Call us at 9184078932 or email accesorios7g@gmail.com. Get your free quote today!"
        keywords="contact cleaning service, cleaning quote, professional cleaning contact, cleaning service inquiry"
        schema={schema}
      />
      
      {/* Header */}
      <Header />

      <main className="px-4">
        {/* Hero Section */}
        <section className="text-center mb-8">
          <h1 className="text-3xl font-bold text-gray-800 mb-4">
            Contact Us
          </h1>
          <p className="text-gray-600 text-lg">
            We are here to help you with all your cleaning services
          </p>
        </section>

        {/* Contact Info Cards */}
        <section className="mb-8">
          <div className="grid grid-cols-1 gap-4">
            <Card className="p-6 text-center">
              <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-phone-line text-blue-600 text-xl"></i>
              </div>
              <h3 className="font-semibold text-gray-800 mb-2">Phone</h3>
              <p className="text-gray-600">9184078932</p>
              <p className="text-gray-500 text-sm mt-1">Text messages only</p>
            </Card>

            <Card className="p-6 text-center">
              <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-mail-line text-green-600 text-xl"></i>
              </div>
              <h3 className="font-semibold text-gray-800 mb-2">Email</h3>
              <p className="text-gray-600">accesorios7g@gmail.com</p>
            </Card>

            <Card className="p-6 text-center">
              <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-map-pin-line text-purple-600 text-xl"></i>
              </div>
              <h3 className="font-semibold text-gray-800 mb-2">Location</h3>
              <p className="text-gray-600">United States</p>
            </Card>
          </div>
        </section>

        {/* Contact Form */}
        <section className="mb-8">
          <Card className="p-6">
            <h2 className="text-2xl font-bold text-gray-800 mb-6 text-center">
              Request Your Quote
            </h2>
            
            {submitStatus === 'success' && (
              <div className="mb-6 p-4 bg-green-100 border border-green-400 text-green-700 rounded-lg">
                Message sent successfully! We will contact you soon.
              </div>
            )}
            
            {submitStatus === 'error' && (
              <div className="mb-6 p-4 bg-red-100 border border-red-400 text-red-700 rounded-lg">
                Error sending message. Please try again.
              </div>
            )}

            <form onSubmit={handleSubmit} data-readdy-form className="space-y-4">
              <div>
                <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                  Full name *
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                  placeholder="Your full name"
                />
              </div>

              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                  Email address *
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                  placeholder="your@email.com"
                />
              </div>

              <div>
                <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2">
                  Phone
                </label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                  placeholder="+1 555 123 4567"
                />
              </div>

              <div>
                <label htmlFor="service" className="block text-sm font-medium text-gray-700 mb-2">
                  Service type *
                </label>
                <select
                  id="service"
                  name="service"
                  value={formData.service}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm bg-white"
                >
                  <option value="">Select a service</option>
                  <option value="home-cleaning">Home Cleaning</option>
                  <option value="office-cleaning">Office Cleaning</option>
                  <option value="deep-cleaning">Deep Cleaning</option>
                  <option value="move-cleaning">Move-in/Move-out Cleaning</option>
                  <option value="post-construction">Post-Construction Cleaning</option>
                  <option value="regular-maintenance">Regular Maintenance</option>
                  <option value="other">Other</option>
                </select>
              </div>

              <div>
                <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-2">
                  Additional message
                </label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleInputChange}
                  rows={4}
                  maxLength={500}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm resize-none"
                  placeholder="Tell us more details about the service you need..."
                ></textarea>
                <p className="text-xs text-gray-500 mt-1">
                  {formData.message.length}/500 characters
                </p>
              </div>

              <button
                type="submit"
                disabled={isSubmitting}
                className={`font-medium rounded-full transition-all duration-200 flex items-center justify-center shadow-lg hover:shadow-xl px-8 py-4 text-lg w-full ${
                  isSubmitting 
                    ? 'bg-gray-400 text-white cursor-not-allowed' 
                    : 'bg-blue-600 text-white hover:bg-blue-700'
                }`}
              >
                {isSubmitting ? (
                  <>
                    <i className="ri-loader-4-line mr-2 animate-spin"></i>
                    Sending...
                  </>
                ) : (
                  <>
                    <i className="ri-send-plane-line mr-2"></i>
                    Send message
                  </>
                )}
              </button>
            </form>
          </Card>
        </section>

        {/* WhatsApp Section */}
        <section className="text-center">
          <Card className="p-6">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <i className="ri-whatsapp-line text-green-600 text-2xl"></i>
            </div>
            <h3 className="text-xl font-bold text-gray-800 mb-2">
              Need immediate response?
            </h3>
            <p className="text-gray-600 mb-4">
              Contact us on WhatsApp for quick assistance
            </p>
            <Button 
              variant="outline" 
              className="border-green-500 text-green-600 hover:bg-green-50"
            >
              <i className="ri-whatsapp-line mr-2"></i>
              Write on WhatsApp
            </Button>
          </Card>
        </section>
      </main>

      {/* Bottom Navigation */}
      <BottomNav />

      {/* WhatsApp Float */}
      <WhatsAppFloat />
    </div>
  );
}
